import { getMemberCareSpecialistOwnProfile } from '@/services/api/requests/member-care';
import { userGetForBoot } from '@/services/api/requests/user';
import { useAppStore, TSessionBundle } from '@/store/useAppStore';
import { TUser } from '@/types/User.types';
import roles from '@/constants/roles';

/**
 * Requests that must not end the session when they fail. They still refresh and
 * replay — a merely-expired access token should recover, not degrade every slice
 * — but an outage in one of them is a data problem, not an auth problem.
 */
const DEGRADABLE = { skipSessionTeardown: true } as const;

const settled = <T>(result: PromiseSettledResult<T>): T | undefined =>
  result.status === 'fulfilled' ? result.value : undefined;

/**
 * Fetch everything the route guards read, with per-slice results.
 *
 * Only the user is required: it is what proves the session, and its failure is
 * the caller's to interpret. memberCare is allSettled, so an outage there cannot
 * take the whole boot down — the same request set sign-in uses, with the opposite
 * failure policy.
 *
 * memberCare is not optional detail: isOnboarded is derived from the user, but the
 * profile screens read this slice directly, and a blank one leaves their fields
 * empty after a reload.
 *
 * Deliberately *not* handleUserDataFlow: that throws on the wrong-platform branch,
 * which is right for a form submit and wrong for a page load.
 */
export const fetchSessionBundle = async (user: TUser): Promise<TSessionBundle> => {
  if (!user.roles.includes(roles.memberCare)) return { user };

  const [memberCare] = await Promise.allSettled([getMemberCareSpecialistOwnProfile(DEGRADABLE)]);

  return { user, memberCare: settled(memberCare)?.data };
};

/** The user fetch itself — separated so the caller owns its failure policy. */
export const fetchBootUser = (): Promise<TUser> => userGetForBoot();

/**
 * Write a bundle to the store.
 *
 * A slice that did not resolve arrives as `undefined` and is left alone rather
 * than written as empty, because an empty memberCare reads as "not onboarded".
 * The caller must not apply a partial bundle when the persisted user differs from
 * the verified one — see setAppStoreOnRehydrate.
 */
export const applySessionBundle = (bundle: TSessionBundle): void =>
  useAppStore.getState().setAppStoreOnRehydrate(bundle);
